import { signPdfBytes, addSignaturePlaceholder, ensureCryptoReady } from '../core-bridge/index.js';
import type { PdfSignOptions, SignatureAlgorithm } from '../core-bridge/index.js';
import { type ParsedArgs, getStringFlag, getStringFlagAll, hasFlag } from '../utils/args.js';
import { readFileOrStdin, writeOutput } from '../utils/io.js';
import { CliError, ErrorCode } from '../utils/error.js';
import { emitStatus, isDryRun } from '../utils/agent.js';
import {
    loadRsaPrivateKey,
    loadEcPrivateKey,
    loadCertificate,
    loadPem,
    loadPemChain,
    parseCertificateChain,
    createNativeCryptoProvider,
} from '../utils/keys.js';

const VALID_ALGORITHMS = new Set<SignatureAlgorithm>(['rsa-sha256', 'ecdsa-sha256']);

function parseSigningTime(raw: string): Date {
    const t = new Date(raw);
    if (Number.isNaN(t.getTime())) {
        throw new CliError(`Invalid --signing-time "${raw}". Expected ISO 8601 (e.g. 2026-04-28T12:00:00Z).`, 2);
    }
    return t;
}

/** Validate that a flag value is a well-formed http(s) URL. */
function assertHttpUrl(value: string, flag: string): void {
    let url: URL;
    try {
        url = new URL(value);
    } catch {
        throw new CliError(`Invalid --${flag} URL "${value}".`, 2);
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw new CliError(`--${flag} must be an http(s) URL, got "${url.protocol}".`, 2);
    }
}

export async function sign(args: ParsedArgs): Promise<void> {
    const inputPath = getStringFlag(args.flags, 'input', 'i');
    const outputPath = getStringFlag(args.flags, 'output', 'o');
    const keyPath = getStringFlag(args.flags, 'key');
    const certPath = getStringFlag(args.flags, 'cert');
    const algorithm = (getStringFlag(args.flags, 'algorithm') ?? 'rsa-sha256') as SignatureAlgorithm;
    const reason = getStringFlag(args.flags, 'reason');
    const name = getStringFlag(args.flags, 'name');
    const location = getStringFlag(args.flags, 'location');
    const contactInfo = getStringFlag(args.flags, 'contact');
    const signingTimeRaw = getStringFlag(args.flags, 'signing-time');
    const chainPaths = getStringFlagAll(args.flags, 'cert-chain');
    const timestampUrl = getStringFlag(args.flags, 'timestamp');
    const pureCrypto = hasFlag(args.flags, 'pure-crypto');
    const dryRun = hasFlag(args.flags, 'dry-run') || isDryRun();

    if (!VALID_ALGORITHMS.has(algorithm)) {
        throw new CliError(
            `Invalid --algorithm "${algorithm}". Valid: rsa-sha256, ecdsa-sha256.`,
            2,
        );
    }

    // Sign-side RFC 3161 timestamping (PAdES-T) requires timestamp-token
    // embedding inside the CMS SignedData — PDF-writing logic that belongs in
    // pdfnative and is not yet exposed (≤ 1.2.0). We surface the flag so the
    // CLI contract is stable, but fail clearly rather than silently ignoring
    // it. Verify-side timestamp validation IS supported (`pdfnative verify`).
    if (timestampUrl !== undefined) {
        assertHttpUrl(timestampUrl, 'timestamp');
        throw new CliError(
            'Sign-side RFC 3161 timestamping (PAdES-T) is not yet available: embedding a '
            + 'timestamp token at signing time requires upstream support in pdfnative '
            + '(tracked at https://github.com/pdfnative/pdfnative/issues). '
            + 'Timestamp VALIDATION is already supported — run `pdfnative verify` on a '
            + 'timestamped PDF.',
            2,
            ErrorCode.UNSUPPORTED,
        );
    }

    // Validate scalar flags up-front so usage errors (exit 2) are reported
    // before any I/O or expensive PEM parsing.
    const signingTime = signingTimeRaw !== undefined ? parseSigningTime(signingTimeRaw) : undefined;

    // Pre-flight: assert credentials are reachable BEFORE doing any expensive parsing.
    // This guarantees a usage error (exit 2) is reported when a flag/env var is missing,
    // even if a partial set of credentials would parse successfully.
    if (process.env['PDFNATIVE_SIGN_KEY'] === undefined && keyPath === undefined) {
        throw new CliError('Missing private key. Provide $PDFNATIVE_SIGN_KEY (env) or --key <path>.', 2);
    }
    if (process.env['PDFNATIVE_SIGN_CERT'] === undefined && certPath === undefined) {
        throw new CliError('Missing certificate. Provide $PDFNATIVE_SIGN_CERT (env) or --cert <path>.', 2);
    }

    // Async crypto bootstrap MUST run before any RSA/ECDSA key parsing.
    // pdfnative throws "ASN.1 module must be imported" otherwise.
    await ensureCryptoReady();

    const pdfBuf = await readFileOrStdin(inputPath);
    let pdfBytes: Uint8Array<ArrayBufferLike> = new Uint8Array(pdfBuf);

    // Load credentials. Env vars beat file flags (OWASP best practice).
    const signerCert = await loadCertificate('PDFNATIVE_SIGN_CERT', certPath, 'cert');

    // Optional intermediate-CA chain
    const chainPemBlocks = await loadPemChain('PDFNATIVE_SIGN_CHAIN', chainPaths);
    const certChain = chainPemBlocks.length > 0 ? parseCertificateChain(chainPemBlocks) : undefined;

    const options: { -readonly [K in keyof PdfSignOptions]: PdfSignOptions[K] } = {
        signerCert,
        algorithm,
    };
    // Signing engine. By default the CLI routes CMS signing through a native,
    // constant-time (side-channel-resistant) OpenSSL signer via node:crypto.
    // `--pure-crypto` forces pdfnative's pure-JS RSA/ECDSA math (useful for
    // reproducibility or environments without node:crypto).
    if (pureCrypto) {
        if (algorithm === 'ecdsa-sha256') {
            options.ecKey = await loadEcPrivateKey('PDFNATIVE_SIGN_KEY', keyPath, 'key');
        } else {
            options.rsaKey = await loadRsaPrivateKey('PDFNATIVE_SIGN_KEY', keyPath, 'key');
        }
    } else {
        const keyPem = await loadPem('PDFNATIVE_SIGN_KEY', keyPath, 'private key', 'key');
        options.provider = createNativeCryptoProvider(keyPem);
    }
    if (certChain !== undefined) options.certChain = certChain;
    if (reason !== undefined) options.reason = reason;
    if (name !== undefined) options.name = name;
    if (location !== undefined) options.location = location;
    if (contactInfo !== undefined) options.contactInfo = contactInfo;
    if (signingTime !== undefined) options.signingTime = signingTime;

    // Auto-inject a signature placeholder when the input PDF doesn't already
    // carry one (the common case for `pdfnative render`-produced PDFs, which
    // ship no AcroForm). pdfnative's addSignaturePlaceholder is idempotent:
    // a PDF that already carries a /FT /Sig widget is returned unchanged.
    try {
        pdfBytes = addSignaturePlaceholder(pdfBytes);
    } catch (e) {
        if (e instanceof CliError) throw e;
        throw new CliError('Failed to prepare PDF for signing.', 1, ErrorCode.SIGN);
    }

    // Dry-run: credentials parsed, PDF read and placeholder-prepared. Stop
    // before producing (or writing) a signature. No key material is touched
    // beyond the validation already performed above.
    if (dryRun) {
        emitStatus({ command: 'sign', dryRun: true, algorithm, output: outputPath ?? '-' });
        return;
    }

    let signedBytes: Uint8Array;
    try {
        signedBytes = signPdfBytes(pdfBytes, options);
    } catch (e) {
        // Never include the underlying message — it may reference key bytes or hashes.
        if (e instanceof CliError) throw e;
        throw new CliError('Failed to sign PDF.', 1, ErrorCode.SIGN);
    }
    await writeOutput(signedBytes, outputPath);
    emitStatus({
        command: 'sign',
        dryRun: false,
        algorithm,
        output: outputPath ?? '-',
        bytes: signedBytes.length,
    });
}
